import { Helpers } from '@bode-canada/core';
import ReactiveState from '../../../hooks/ReactiveState';
import { FormControlState, FormControlValidationStrategy } from './enums';
import { FormCommonValidatorName } from '../validators/enums';

class FormControl {
  #reactiveState;
  #validators;

  constructor(
    defaultValue,
    validators = [],
    validationStrategy = FormControlValidationStrategy.Blur,
  ) {
    this.#validators = validators;
    this.#reactiveState = new ReactiveState({
      [FormControlState.Value]: defaultValue,
      [FormControlState.Error]: '',
      [FormControlState.IsValid]: true,
      [FormControlState.IsDisabled]: false,
      [FormControlState.ValidationStrategy]: validationStrategy,
    });
  }

  get value() {
    return this.#reactiveState.value[FormControlState.Value];
  }

  get initialValue() {
    return this.#reactiveState.initialState[FormControlState.Value];
  }

  get isChanged() {
    return !Helpers.ObjectHelper.isEqual(this.value, this.initialValue);
  }

  get error() {
    return this.#reactiveState.value[FormControlState.Error];
  }

  get validationStrategy() {
    return this.#reactiveState.value[FormControlState.ValidationStrategy];
  }

  get isValid() {
    return this.#reactiveState.value[FormControlState.IsValid];
  }

  get isDisabled() {
    return this.#reactiveState.value[FormControlState.IsDisabled];
  }

  get isRequired() {
    return !!this.#validators.find((validator) => (
      validator.name === FormCommonValidatorName.Required
    ));
  }

  setValidationStrategy(validationStrategy) {
    this.#reactiveState.patch({
      [FormControlState.ValidationStrategy]: validationStrategy,
    });
  }

  addValidators(validators) {
    this.#validators = [...this.#validators, ...validators];
  }

  setValidators(validators) {
    this.#validators = validators;
  }

  setValue(value) {
    this.#reactiveState.patch({
      [FormControlState.Value]: value,
    });
  }

  setInitialValue(value) {
    this.#reactiveState.patchInitialValue({
      [FormControlState.Value]: value,
    });
  }

  setError(error) {
    this.#reactiveState.patch({
      [FormControlState.Error]: error,
    });
  }

  setIsValid(isValid) {
    this.#reactiveState.patch({
      [FormControlState.IsValid]: isValid,
    });
  }

  disable() {
    this.#setIsDisabled(true);
  }

  enable() {
    this.#setIsDisabled(false);
  }

  reset(value) {
    if (value) {
      this.#reactiveState.patch({ [FormControlState.Value]: value });
      this.#reactiveState.patchInitialValue({ [FormControlState.Value]: value });

      this.#reactiveState.resetPartially([
        FormControlState.IsValid,
        FormControlState.Error,
      ]);
    } else {
      this.#reactiveState.resetPartially([
        FormControlState.Value,
        FormControlState.IsValid,
        FormControlState.Error,
      ]);
    }
  }

  clean() {
    this.setValue(null);
  }

  getIsValid() {
    return this.#validators.find((validator) => (
      !validator.getIsValid(this.value)
    ));
  }

  validate() {
    const validatorWithError = this.getIsValid();

    if (!validatorWithError) {
      this.#reactiveState.resetPartially([
        FormControlState.IsValid,
        FormControlState.Error,
      ]);

      return;
    }

    this.setIsValid(false);
    this.setError(validatorWithError.errorMessage);
  }

  #setIsDisabled(isDisabled) {
    this.#reactiveState.patch({
      [FormControlState.IsDisabled]: isDisabled,
    });
  }
}

export default FormControl;
